import { useApiWithAuth } from '@/hooks/api';
import { usePeopleStore } from '@/store/people';
import { loadPolicy } from '@/store/access/actions';
import type {
  LoadPeopleParams,
  LoadPeopleSearchParams,
} from '@/store/people/actions/people';
import { loadPeople, loadPerson } from '@/store/people/actions/people';
import { isLoadParamsEqual } from '@/store/people/helpers';
import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAccessStore } from '@/store/access';
import type { Person } from '@/store/people/types';
import { useAuth } from '@/hooks/auth';

export function usePerson(profileId: string) {
  const store = usePeopleStore();
  const { api, auth } = useApiWithAuth();
  const [loading, setLoading] = useState(false);
  const [person, setPerson] = useState<Person | null>(null);
  const loaded = useRef(false);

  const loadPersonData = useCallback(async () => {
    if (loaded.current || loading) {
      return;
    }

    if (!profileId) {
      setPerson(null);
      setLoading(false);

      return;
    }

    try {
      setLoading(true);
      loaded.current = true;

      const { item } = await loadPerson(store, {
        force: true,
        cache: false,
        api,
        auth,
        id: profileId,
      });

      if (item) {
        setPerson(item);
      } else {
        setPerson(null);
      }
    } finally {
      setLoading(false);
    }
  }, [store, api, auth, profileId, loading]);

  useEffect(() => {
    if (profileId) {
      loadPersonData();
    }
  }, [profileId, loadPersonData]);

  return { loading, person };
}

export function useLoadPeople({
  page,
  setPage,
  itemsPerPage,
  itemLimit,
  campus,
  memberStatus,
  laygroup1,
  laygroup2,
  search,
  tags,
}: {
  page: number;
  setPage: (_page: number) => void;
  itemsPerPage: number;
  itemLimit: number;
  campus?: string;
  memberStatus?: string[];
  laygroup1?: string;
  laygroup2?: string;
  search?: LoadPeopleSearchParams;
  tags?: string[];
}) {
  const store = usePeopleStore();
  const { api, auth } = useApiWithAuth();
  const lastParams = useRef<LoadPeopleParams>({});
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const itemCount = store.state.data.length;

  const params = useMemo(() => {
    const p: LoadPeopleParams = {};
    if (campus) {
      p.criteria = { campus_id: campus };
    }

    if (memberStatus?.length) {
      p.criteria = { ...(p.criteria || {}), member_status: memberStatus };
    }

    if (laygroup1) {
      p.criteria = { ...(p.criteria || {}), groups: { laygroup1 } };
    }

    if (laygroup2) {
      p.criteria = {
        ...(p.criteria || {}),
        groups: { ...(p.criteria?.groups || {}), laygroup2 },
      };
    }

    if (tags) {
      p.criteria = { ...(p.criteria || {}), tags };
    }

    if (search) {
      p.search = search;
    }

    p.order = [{ field: 'fullname', desc: false }];
    p.limit = itemLimit;
    p.offset = Math.max(0, (page - 1) * itemsPerPage);
    p.offset -= p.offset % itemLimit;
    if (!isLoadParamsEqual(lastParams.current, p, ['offset', 'count'])) {
      p.count = true;
      p.offset = 0;
    }

    return p;
  }, [
    campus,
    memberStatus,
    laygroup1,
    laygroup2,
    search,
    tags,
    page,
    itemsPerPage,
    itemLimit,
  ]);

  useEffect(() => {
    let force = false;
    if (!isLoadParamsEqual(lastParams.current, params)) {
      if (
        !params.offset ||
        params.offset >= store.state.data.length ||
        params.offset === lastParams.current.offset
      ) {
        force = true;
      }
    } else if ((params.offset || 0) > itemCount) {
      force = true;
    }

    if (!force && (store.state.loaded || loadingRef.current)) {
      return;
    }

    loadingRef.current = true;
    lastParams.current = params;
    setLoading(true);
    loadPeople(store, { force, api, auth }, params)
      .then(() => {
        setLoading(false);
        loadingRef.current = false;
        if (params.count) {
          setPage(1);
        }

        return;
      })
      .catch((e) => console.error(e));
  }, [store, auth, api, params, itemCount, setPage]);

  return { loading };
}

export function useAccessPolicy() {
  const { authorized } = useAuth();
  const { api, auth } = useApiWithAuth();
  const store = useAccessStore();
  const [loading, setLoading] = useState(true);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current || !authorized) {
      return;
    }

    loaded.current = true;
    const load = async () => {
      setLoading(true);
      await loadPolicy(store, { api, auth });
      setLoading(false);
    };

    load();
  }, [store, api, auth, authorized]);

  return {
    policy: store.state.data,
    loaded: store.state.loaded,
    loading,
  };
}

export function useProfileDetails(profileData?: Person['data']) {
  const [searchParams] = useSearchParams();
  const data = searchParams.get('data');

  const parsedData = useMemo(() => {
    if (profileData) {
      return profileData;
    }

    if (data) {
      return JSON.parse(data as string);
    }

    return null;
  }, [profileData, data]);

  const getProfileDetails = (data: Person['data'] | null) => {
    if (!data) {
      return {
        fullname: 'N/A',
        firstName: 'N/A',
        lastName: 'N/A',
        preferredName: 'N/A',
        email: 'N/A',
        phone: 'N/A',
        campus: 'N/A',
        gender: 'N/A',
        memberStatus: 'N/A',
        maritalStatus: 'N/A',
        archived: false,
        birthdate: 'N/A',
        householdId: 'N/A',
        groups: {},
        updated: 'N/A',
        address: 'N/A',
        uid: 'N/A',
        avatar: '',
        bio: 'N/A',
      };
    }

    return {
      fullname: data.fullname || 'N/A',
      firstName: data.firstname || 'N/A',
      lastName: data.lastname || 'N/A',
      preferredName: data.preferredname || 'N/A',
      email: data.email || 'N/A',
      phone: data.phone || '',
      campus: data.campus_id || 'N/A',
      gender: data.gender || 'N/A',
      memberStatus: data.member_status || 'N/A',
      maritalStatus: data.marital_status || 'N/A',
      archived: data.archived || false,
      birthdate: data.birthdate || 'N/A',
      householdId: data.household_id || 'N/A',
      groups: data.groups || {},
      updated: data.updated || 'N/A',
      address: data.address || 'N/A',
      uid: data.uid || 'N/A',
      avatar: '',
      bio: 'N/A',
    };
  };

  return getProfileDetails(parsedData);
}
